import { useEffect, useState } from 'react';
import { redirect, type MetaFunction, Form, useSearchParams } from 'react-router';
import { SocialsProvider } from 'remix-auth-socials';
import { AuthenticityTokenInput } from 'remix-utils/csrf/react';

import { AppInfoCard } from '~/components/home-page/app-info-card';
import { PublicNavbar } from '~/components/layout/public-navbar';
import { FacebookLogo } from '~/components/social/facebook-logo';
import { GoogleLogo } from '~/components/social/google-logo';
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselControls,
  CarouselItem,
  useScrollIntoView,
} from '~/components/ui/carousel';
import { StatusButton } from '~/components/ui/status-button.tsx';
import { cn } from '~/styles/extended-theme';
import { getSessionId } from '~/utils/auth.server';
import { useIsPending } from '~/utils/misc.ts';
import { useIsBelowBreakpoint } from '~/utils/use-is-below-breakpoint';

import { type Route } from './+types/login';

export const meta: MetaFunction = () => {
  return [{ title: 'Log in to Fighters Passport' }];
};

export async function loader({ request }: Route.ActionArgs) {
  const sessionId = await getSessionId(request);

  if (sessionId) {
    // go to root page if already logged in
    return redirect('/');
  }

  return {};
}

function SignInAppInfoSection() {
  const [api, setApi] = useState<CarouselApi>();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const scrollIntoView = useScrollIntoView(api);

  const isMobile = useIsBelowBreakpoint(768);

  useEffect(() => {
    setSelectedIndex(0);
  }, [isMobile]);

  useEffect(() => {
    const callback = () => {
      const slidesInView = api?.slidesInView();

      if (slidesInView === undefined) {
        return;
      }

      const nextIndex = slidesInView[0] === 0 ? 0 : 1;
      scrollIntoView(nextIndex);
      setSelectedIndex(nextIndex);
    };

    api?.on('pointerUp', callback);

    return () => {
      api?.off('pointerUp', callback);
    };
  }, [api, scrollIntoView]);

  return isMobile ? (
    <Carousel setApi={setApi} className="mb-4 w-full rounded-2xl border border-gray-400 bg-white">
      <CarouselContent>
        <CarouselItem>
          <AppInfoCard isAthleteSection={true} hideButton className="w-full p-6" />
        </CarouselItem>
        <CarouselItem>
          <AppInfoCard isAthleteSection={false} hideButton className="w-full p-6" />
        </CarouselItem>
      </CarouselContent>
      <CarouselControls
        className="-bottom-6 left-1/2 w-[60px] -translate-x-1/2"
        buttonClassName="bg-gray-200"
        activeButtonClassName="bg-destructive"
        selected={selectedIndex}
        onSelected={setSelectedIndex}
      />
    </Carousel>
  ) : (
    <div className="flex w-full rounded-2xl border border-gray-400 bg-white desktop:w-[664px]">
      <AppInfoCard isAthleteSection hasRightBorder hideButton className="p-6" />
      <AppInfoCard isAthleteSection={false} hideButton className="p-6" />
    </div>
  );
}

export default function Login() {
  const [searchParams] = useSearchParams();
  const loading = useIsPending();

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden">
      <PublicNavbar />
      <div className="absolute right-1/2 -z-10 translate-x-1/2 overflow-hidden desktop:-top-[-40px] desktop:translate-x-[70px]">
        <img
          src="/img/gymowner_bg.png"
          alt="gym owner bg"
          className={cn(
            'opacity-[25%]',
            'h-[344px] w-[516px] min-w-[516px]',
            'tablet:h-[512px] tablet:w-[768px] tablet:min-w-[768px]',
            'desktop:h-[647px] desktop:w-[970px] desktop:min-w-[970px]',
          )}
        />
      </div>
      <div className="mx-auto mt-8 flex w-[329px] flex-col gap-4 tablet:mt-[135px] tablet:w-[410px] desktop:mt-[185px] desktop:w-[1056px]">
        <p className="font-mono text-h1 tracking-[1.5px] text-primary">Unleash Your Potential</p>
        <div className="flex flex-col gap-8 desktop:flex-row">
          <SignInAppInfoSection />
          <div className="flex w-full flex-col gap-4 desktop:w-[360px] desktop:pt-4">
            <p className="text-subtitle1 text-black">
              Unlock all of our great features by signing in or creating an account.
            </p>

            <Form action={`/auth/${SocialsProvider.GOOGLE}`} method="post">
              <AuthenticityTokenInput />
              <input
                type="hidden"
                name="redirectTo"
                value={searchParams.get('redirectTo') ?? undefined}
              />
              <StatusButton
                type="submit"
                variant="outline"
                className="h-[42px] w-full text-subtitle1 font-bold"
                status={loading ? 'pending' : 'idle'}
              >
                <div className="flex items-center">
                  <GoogleLogo className="mr-2 inline" />
                  Continue with Google
                </div>
              </StatusButton>
            </Form>

            <Form action={`/auth/${SocialsProvider.FACEBOOK}`} method="post">
              <AuthenticityTokenInput />
              <input
                type="hidden"
                name="redirectTo"
                value={searchParams.get('redirectTo') ?? undefined}
              />
              <StatusButton
                type="submit"
                variant="outline"
                className="h-[42px] w-full text-subtitle1 font-bold"
                status={loading ? 'pending' : 'idle'}
              >
                <div className="flex items-center">
                  <FacebookLogo className="mr-2 inline" />
                  Continue with Facebook
                </div>
              </StatusButton>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
}
